import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { rethrowIfUnauthorized } from '../api/errors'
import { getTimelineSession, putTimelineSession } from '../api/timelineSession'
import { createLogger } from '../lib/logger'
import { randomUuid } from '../lib/randomUuid'
import { normalizeTimelineRound, normalizeTimelineRounds } from '../lib/timelineRoundNormalize'
import type { GenItem, TimelineRound } from '../types/timeline'

const timelineLogger = createLogger('timeline')

type TimelineState = {
  sessionId: string
  rounds: TimelineRound[]
}

type TimelineContextValue = {
  /** 时间线是否已从后端加载完成 */
  hydrated: boolean
  /** 是否正在加载 */
  loading: boolean
  /** 当前会话ID */
  sessionId: string | null
  /** 时间线轮次数据 */
  rounds: TimelineRound[]
  /** 主动刷新时间线（从后端重新加载），返回本次拉到的轮次 */
  refresh: () => Promise<TimelineRound[]>
  /** 添加新的轮次到时间线（同时保存到后端） */
  addRound: (round: TimelineRound) => void
  /** 若存在同 id 则替换，否则插入到头部 */
  upsertRound: (round: TimelineRound) => void
  /** 删除指定 id 的轮次（仅从时间线移除，不影响后端 creation_tasks） */
  removeRound: (roundId: string) => void
  /**
   * 把 items 追加到目标轮次末尾，并在同一次状态更新里丢弃临时轮次。
   * 追加生成必须走这里：读写同一个 prev 快照，避免闭包里拿到旧 rounds 把原图覆盖掉，
   * 也避免「先合并、再删除」两次整份 PUT 造成刷新后多出一条时间线。
   */
  appendItemsToRound: (roundId: string, items: GenItem[], dropRoundId?: string) => void
  /** 替换整个时间线数据（用于后端同步） */
  replaceRounds: (rounds: TimelineRound[]) => void
}

const TimelineContext = createContext<TimelineContextValue | null>(null)

export function TimelineProvider({ children }: { children: ReactNode }) {
  const [hydrated, setHydrated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [rounds, setRounds] = useState<TimelineRound[]>([])
  const sessionIdRef = useRef<string | null>(null)

  // 从后端加载时间线（默认对账创作任务）
  const loadFromBackend = useCallback(async (reconcile = true): Promise<TimelineRound[]> => {
    setLoading(true)
    try {
      const res = await getTimelineSession({ reconcile })
      const st = res.state
      const raw = Array.isArray(st.rounds) ? st.rounds : []
      const nextRounds = normalizeTimelineRounds(raw).filter((r) => !r.id.startsWith('demo-tl-'))
      sessionIdRef.current = st.sessionId
      setSessionId(st.sessionId)
      setRounds(nextRounds)
      setHydrated(true)
      return nextRounds
    } catch (error) {
      rethrowIfUnauthorized(error)
      // 后端加载失败时，保持现有状态（如果有的话）
      timelineLogger.error('从后端加载失败', error)
      // 如果没有现有状态，创建新的会话
      if (!sessionIdRef.current) {
        const newSessionId = randomUuid()
        sessionIdRef.current = newSessionId
        setSessionId(newSessionId)
        setRounds([])
        setHydrated(true)
        return []
      }
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  // 初始加载
  useEffect(() => {
    void loadFromBackend()
  }, [loadFromBackend])

  /**
   * 保存到后端 - 严格以后端为准，保存失败不更新前端状态。
   *
   * 串行化写入：PUT 是整份状态覆盖，若并发发出（追加生成时「合并 + 删除临时轮次」会连续写两次），
   * 先发的请求后到达就会把旧状态写回去，表现为刷新后多出一条时间线。
   * 这里维护单一在途写 + 最新快照，保证「后写覆盖先写」的顺序与最终一致性。
   */
  const pendingSaveRef = useRef<TimelineState | null>(null)
  const savingRef = useRef(false)

  const flushPendingSave = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    try {
      while (pendingSaveRef.current) {
        const next = pendingSaveRef.current
        pendingSaveRef.current = null
        try {
          await putTimelineSession(next)
        } catch (error) {
          rethrowIfUnauthorized(error)
          timelineLogger.error('保存到后端失败', error)
        }
      }
    } finally {
      savingRef.current = false
    }
  }, [])

  const saveToBackend = useCallback(
    (state: TimelineState) => {
      pendingSaveRef.current = state
      void flushPendingSave()
    },
    [flushPendingSave],
  )

  // 添加新轮次
  const addRound = useCallback((round: TimelineRound) => {
    setRounds((prev) => {
      const normalized = normalizeTimelineRound(round)
      const next = [normalized, ...prev]
      const sid = sessionIdRef.current
      if (sid) {
        // 异步保存到后端，但不等待结果
        void saveToBackend({ sessionId: sid, rounds: next })
      }
      return next
    })
  }, [saveToBackend])

  const upsertRound = useCallback((round: TimelineRound) => {
    setRounds((prev) => {
      const normalized = normalizeTimelineRound(round)
      const idx = prev.findIndex((r) => r.id === normalized.id)
      const next =
        idx >= 0
          ? prev.map((r, i) => (i === idx ? normalized : r))
          : [normalized, ...prev]
      const sid = sessionIdRef.current
      if (sid) {
        void saveToBackend({ sessionId: sid, rounds: next })
      }
      return next
    })
  }, [saveToBackend])

  const removeRound = useCallback((roundId: string) => {
    setRounds((prev) => {
      const next = prev.filter((r) => r.id !== roundId)
      const sid = sessionIdRef.current
      if (sid) {
        void saveToBackend({ sessionId: sid, rounds: next })
      }
      return next
    })
  }, [saveToBackend])

  const appendItemsToRound = useCallback(
    (roundId: string, items: GenItem[], dropRoundId?: string) => {
      if (!items.length && !dropRoundId) return
      setRounds((prev) => {
        const hasTarget = prev.some((r) => r.id === roundId)
        const next = prev
          // 目标轮次还在才丢弃临时载体；目标已消失则保留，避免图片丢失
          .filter((r) => !hasTarget || !dropRoundId || r.id !== dropRoundId)
          .map((r) =>
            r.id === roundId
              ? normalizeTimelineRound({
                  ...r,
                  items: [...r.items, ...items.map((item) => ({ ...item, roundId }))],
                })
              : r,
          )
        const sid = sessionIdRef.current
        if (sid) {
          void saveToBackend({ sessionId: sid, rounds: next })
        }
        return next
      })
    },
    [saveToBackend],
  )

  // 替换所有轮次（用于需要完全同步的场景）
  const replaceRounds = useCallback((newRounds: TimelineRound[]) => {
    setRounds(newRounds)
  }, [])

  // 刷新 - 从后端重新加载并对账
  const refresh = useCallback(async () => {
    return loadFromBackend(true)
  }, [loadFromBackend])

  const value = useMemo(
    () => ({
      hydrated,
      loading,
      sessionId,
      rounds,
      refresh,
      addRound,
      upsertRound,
      removeRound,
      appendItemsToRound,
      replaceRounds,
    }),
    [
      hydrated,
      loading,
      sessionId,
      rounds,
      refresh,
      addRound,
      upsertRound,
      removeRound,
      appendItemsToRound,
      replaceRounds,
    ]
  )

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  )
}

export function useTimeline() {
  const ctx = useContext(TimelineContext)
  if (!ctx) throw new Error('useTimeline 须在 TimelineProvider 内使用')
  return ctx
}
